import { Directive, DestroyRef, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RoutePreloadIntentService } from '@quipex/core/http';
import type { Maybe } from '@quipex/core/primitives';
import { injectSupportsHoverInteraction } from '@quipex/core/utils';
import { merge, Subject, switchMap, takeUntil, timer } from 'rxjs';

const HOVER_PRELOAD_DELAY_MS = 150;

interface NavigatorWithConnection extends Navigator {
    readonly connection?: {
        readonly saveData?: boolean;
    };
}

@Directive({
    selector: '[qpxPreloadOnIntent]',
    host: {
        '(pointerenter)': 'onPointerEnter()',
        '(pointerleave)': 'onPointerLeave()',
        '(focus)': 'onFocus()',
    },
})
export class PreloadOnIntent {
    //#region Dependency injection
    private readonly destroyRef = inject(DestroyRef);
    private readonly preloadIntent = inject(RoutePreloadIntentService);
    private readonly supportsHoverInteraction =
        injectSupportsHoverInteraction();
    //#endregion

    //#region State
    private readonly hoverIntent$ = new Subject<void>();
    private readonly hoverCancel$ = new Subject<void>();
    private readonly focusIntent$ = new Subject<void>();
    //#endregion

    //#region Inputs
    public readonly qpxPreloadOnIntent = input<Maybe<string>>();
    //#endregion

    //#region Lifecycle hooks
    constructor() {
        merge(
            this.hoverIntent$.pipe(
                switchMap(() =>
                    timer(HOVER_PRELOAD_DELAY_MS).pipe(
                        takeUntil(this.hoverCancel$)
                    )
                )
            ),
            this.focusIntent$
        )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.requestPreload());

        this.destroyRef.onDestroy(() => {
            this.hoverIntent$.complete();
            this.hoverCancel$.complete();
            this.focusIntent$.complete();
        });
    }
    //#endregion

    //#region Host event handlers
    protected onPointerEnter(): void {
        if (!this.supportsHoverInteraction()) return;
        if (this.shouldSkipPreload()) return;

        this.hoverIntent$.next();
    }

    protected onPointerLeave(): void {
        this.hoverCancel$.next();
    }

    protected onFocus(): void {
        this.hoverCancel$.next();
        this.focusIntent$.next();
    }
    //#endregion

    //#region Private methods
    private requestPreload(): void {
        const key = this.qpxPreloadOnIntent()?.trim();
        if (key === undefined || key === '' || this.shouldSkipPreload()) {
            return;
        }

        this.preloadIntent.request(key);
    }

    private shouldSkipPreload(): boolean {
        const key = this.qpxPreloadOnIntent()?.trim();
        return (
            key === undefined ||
            key === '' ||
            this.preloadIntent.hasRequested(key) ||
            this.prefersReducedData()
        );
    }

    private prefersReducedData(): boolean {
        if (typeof navigator === 'undefined') return false;

        return (
            (navigator as NavigatorWithConnection).connection?.saveData === true
        );
    }
    //#endregion
}
